import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import {
  BLUE_MAX,
  PRICE_PER_TICKET,
  RED_MAX,
  calculateMetrics,
  combinationCount,
  expandDantuo,
  expandMultiple,
  formatNumber,
  generateConditional,
  quickGenerate,
  ticketKey,
  ticketsText,
} from './lottery'
import {
  EMPTY_OTHER_CONSTRAINTS,
  WELFARE_GAMES,
  analyzeOtherNumbers,
  calculateOtherDantuoCount,
  calculateOtherMultipleCount,
  generateOtherGame,
  otherTicketsText,
  type Fc3dMode,
  type OtherGameConstraints,
  type OtherGameTicket,
  type OtherGenerationMode,
  type WelfareGameId,
} from './games'
import { downloadShareImage, type ShareTicket } from './share'
import type { Constraints, Ticket, ZonePreset } from './types'

type Mode = 'quick' | 'conditional' | 'dantuo' | 'multiple'
type BallTone = 'red' | 'blue' | 'gold' | 'violet' | 'teal'

const GAME_ICONS: Record<WelfareGameId, string> = {
  ssq: '双',
  qlc: '七',
  fc3d: '3D',
  kl8: '8',
}

const GAME_TONES: Record<Exclude<WelfareGameId, 'ssq'>, BallTone> = {
  qlc: 'gold',
  fc3d: 'violet',
  kl8: 'teal',
}

const MODES: Array<{ id: Mode; label: string; icon: string; note: string }> = [
  { id: 'quick', label: '好运随机', icon: '✦', note: '轻松摇一摇' },
  { id: 'conditional', label: '条件实验室', icon: '⌁', note: '按结构筛选' },
  { id: 'dantuo', label: '胆拖展开', icon: '◇', note: '胆码 + 拖码' },
  { id: 'multiple', label: '复式展开', icon: '◎', note: '自由圈号' },
]

const OTHER_MODES: Array<{ id: OtherGenerationMode; label: string; icon: string; note: string }> = [
  { id: 'quick', label: '随机生成', icon: '✦', note: '按玩法规则随机' },
  { id: 'conditional', label: '条件筛选', icon: '⌁', note: '和值 / 奇偶 / 必排' },
  { id: 'dantuo', label: '胆拖展开', icon: '◇', note: '固定号码 + 拖码' },
  { id: 'multiple', label: '组合展开', icon: '◎', note: '从选号池展开' },
]

const defaultConstraints: Constraints = {
  count: 5,
  requiredRed: [],
  excludedRed: [],
  excludedBlue: [],
  zonePreset: 'any',
}

function loadSaved<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function toggleNumber(list: number[], value: number) {
  return list.includes(value)
    ? list.filter(n => n !== value)
    : [...list, value].sort((a, b) => a - b)
}

function Ball({
  value,
  blue = false,
  tone,
  pad = true,
  selected = false,
  disabled = false,
  onClick,
  delay = 0,
}: {
  value: number
  blue?: boolean
  tone?: BallTone
  pad?: boolean
  selected?: boolean
  disabled?: boolean
  onClick?: () => void
  delay?: number
}) {
  const resolvedTone = tone ?? (blue ? 'blue' : 'red')
  const displayValue = pad ? formatNumber(value) : String(value)
  const className = ['ball', `ball-${resolvedTone}`, selected ? 'is-selected' : '', onClick ? 'is-clickable' : '']
    .filter(Boolean)
    .join(' ')
  const style = { '--delay': `${delay}ms` } as CSSProperties
  const label = `号码 ${displayValue}`

  if (!onClick) {
    return <span className={className} style={style} aria-label={label}>{displayValue}</span>
  }

  return (
    <button
      type="button"
      className={className}
      disabled={disabled}
      onClick={onClick}
      style={style}
      aria-pressed={selected}
      aria-label={label}
    >
      {displayValue}
    </button>
  )
}

function NumberGrid({
  min = 1,
  max,
  blue = false,
  tone,
  pad = true,
  dense = false,
  selected,
  onToggle,
  disabledNumbers = [],
}: {
  min?: number
  max: number
  blue?: boolean
  tone?: BallTone
  pad?: boolean
  dense?: boolean
  selected: number[]
  onToggle: (n: number) => void
  disabledNumbers?: number[]
}) {
  const values = Array.from({ length: max - min + 1 }, (_, index) => min + index)

  return (
    <div className={dense ? 'number-grid number-grid-dense' : 'number-grid'}>
      {values.map(n => (
        <Ball
          key={n}
          value={n}
          blue={blue}
          tone={tone}
          pad={pad}
          selected={selected.includes(n)}
          disabled={disabledNumbers.includes(n)}
          onClick={() => onToggle(n)}
        />
      ))}
    </div>
  )
}

export default function App() {
  const [game, setGame] = useState<WelfareGameId>('ssq')
  const [mode, setMode] = useState<Mode>('quick')
  const [otherMode, setOtherMode] = useState<OtherGenerationMode>('quick')
  const [constraints, setConstraints] = useState<Constraints>(defaultConstraints)
  const [countDraft, setCountDraft] = useState(String(defaultConstraints.count))
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [otherTickets, setOtherTickets] = useState<OtherGameTicket[]>([])
  const [happyPickCount, setHappyPickCount] = useState(10)
  const [fc3dMode, setFc3dMode] = useState<Fc3dMode>('direct')
  const [otherConstraints, setOtherConstraints] = useState<OtherGameConstraints>({ ...EMPTY_OTHER_CONSTRAINTS })
  const [otherDan, setOtherDan] = useState<number[]>([])
  const [otherTuo, setOtherTuo] = useState<number[]>([])
  const [otherMultiple, setOtherMultiple] = useState<number[]>([])
  const [showOtherAdvanced, setShowOtherAdvanced] = useState(false)
  const [rollingOther, setRollingOther] = useState<OtherGameTicket | null>(null)
  const [warning, setWarning] = useState('')
  const [generating, setGenerating] = useState(false)
  const [rolling, setRolling] = useState<Ticket>({ reds: [1, 7, 13, 19, 25, 31], blue: 8 })
  const [toast, setToast] = useState('')
  const [favorites, setFavorites] = useState<Ticket[]>(() => loadSaved('lottery:favorites', []))
  const [historyKeys, setHistoryKeys] = useState<string[]>(() => loadSaved('lottery:historyKeys', []))
  const [dark, setDark] = useState(() => loadSaved('lottery:dark-v2', false))
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [resultView, setResultView] = useState<'visual' | 'details'>('visual')
  const [dan, setDan] = useState<number[]>([])
  const [tuo, setTuo] = useState<number[]>([])
  const [dantuoBlue, setDantuoBlue] = useState<number[]>([1])
  const [multipleRed, setMultipleRed] = useState<number[]>([])
  const [multipleBlue, setMultipleBlue] = useState<number[]>([1])
  const timerRef = useRef<number | null>(null)
  const finishTimerRef = useRef<number | null>(null)
  const resultRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light'
    localStorage.setItem('lottery:dark-v2', JSON.stringify(dark))
  }, [dark])

  useEffect(() => {
    localStorage.setItem('lottery:favorites', JSON.stringify(favorites))
  }, [favorites])

  useEffect(() => () => {
    if (timerRef.current) window.clearInterval(timerRef.current)
    if (finishTimerRef.current) window.clearTimeout(finishTimerRef.current)
  }, [])

  const historySet = useMemo(() => new Set(historyKeys), [historyKeys])
  const selectedMode = MODES.find(item => item.id === mode)!
  const activeGame = WELFARE_GAMES.find(item => item.id === game)!
  const otherPickCount = game === 'qlc' ? 7 : game === 'fc3d' ? 3 : happyPickCount
  const otherModeOptions = OTHER_MODES.filter(item =>
    game === 'fc3d'
      ? item.id === 'quick' || item.id === 'conditional'
      : !(game === 'kl8' && happyPickCount === 1 && item.id === 'dantuo'),
  )
  const selectedOtherMode = OTHER_MODES.find(item => item.id === otherMode)!
  const resultCount = game === 'ssq' ? tickets.length : otherTickets.length
  const totalPrice = resultCount * PRICE_PER_TICKET
  const redFrequency = useMemo(
    () => Array.from({ length: RED_MAX }, (_, index) =>
      tickets.reduce((count, ticket) => count + (ticket.reds.includes(index + 1) ? 1 : 0), 0),
    ),
    [tickets],
  )
  const blueFrequency = useMemo(
    () => Array.from({ length: BLUE_MAX }, (_, index) =>
      tickets.reduce((count, ticket) => count + (ticket.blue === index + 1 ? 1 : 0), 0),
    ),
    [tickets],
  )
  const maxRedFrequency = Math.max(1, ...redFrequency)
  const maxBlueFrequency = Math.max(1, ...blueFrequency)

  const dantuoCount =
    dan.length > 0 && dan.length < 6 && tuo.length >= 6 - dan.length
      ? combinationCount(tuo.length, 6 - dan.length) * dantuoBlue.length
      : 0
  const multipleCount =
    multipleRed.length >= 6
      ? combinationCount(multipleRed.length, 6) * multipleBlue.length
      : 0
  const otherDantuoCount = game === 'fc3d'
    ? 0
    : calculateOtherDantuoCount(otherPickCount, otherDan, otherTuo)
  const otherMultipleCount = game === 'fc3d'
    ? 0
    : calculateOtherMultipleCount(otherPickCount, otherMultiple)
  const otherNumberMin = game === 'fc3d' ? 0 : 1
  const otherNumberMax = game === 'qlc' ? 30 : game === 'fc3d' ? 9 : 80
  const otherRequiredLimit = game === 'fc3d'
    ? fc3dMode === 'group3' ? 2 : 3
    : otherPickCount
  const otherBigFrom = game === 'qlc' ? 16 : game === 'fc3d' ? 5 : 41
  const activeOtherTone: BallTone = game === 'ssq' ? 'red' : GAME_TONES[game]

  const setCount = (value: number) => {
    const next = Math.min(200, Math.max(1, Math.trunc(value) || 1))
    setConstraints(current => ({ ...current, count: next }))
    setCountDraft(String(next))
  }

  const commitCountDraft = () => {
    const parsed = Number.parseInt(countDraft, 10)
    setCount(Number.isFinite(parsed) ? parsed : constraints.count)
  }

  const switchGame = (nextGame: WelfareGameId) => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (finishTimerRef.current) {
      window.clearTimeout(finishTimerRef.current)
      finishTimerRef.current = null
    }
    setGenerating(false)
    setGame(nextGame)
    setTickets([])
    setOtherTickets([])
    setWarning('')
    setRollingOther(null)
    setResultView('visual')
    setOtherMode('quick')
    setOtherConstraints({ ...EMPTY_OTHER_CONSTRAINTS })
    setOtherDan([])
    setOtherTuo([])
    setOtherMultiple([])
    setShowOtherAdvanced(false)
    if (nextGame !== 'ssq') setMode('quick')
  }

  const flash = (message: string) => {
    setToast(message)
    window.setTimeout(() => setToast(''), 1800)
  }

  const remember = (next: Ticket[]) => {
    if (!next.length) return
    const keys = next.map(ticketKey)
    setHistoryKeys(prev => {
      const merged = [...keys, ...prev.filter(key => !keys.includes(key))].slice(0, 1200)
      localStorage.setItem('lottery:historyKeys', JSON.stringify(merged))
      return merged
    })
  }

  const showResult = (next: Ticket[], nextWarning = '') => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (timerRef.current) window.clearInterval(timerRef.current)

    if (reducedMotion) {
      setTickets(next)
      setWarning(nextWarning)
      remember(next)
      return
    }

    setGenerating(true)
    setTickets([])
    setWarning('')

    timerRef.current = window.setInterval(() => {
      const fake = quickGenerate(1).tickets[0]
      if (fake) setRolling(fake)
    }, 90)

    finishTimerRef.current = window.setTimeout(() => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current)
        timerRef.current = null
      }
      finishTimerRef.current = null
      setGenerating(false)
      setTickets(next)
      setWarning(nextWarning)
      remember(next)
      window.setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 120)
    }, 1050)
  }

  const showOtherResult = (next: OtherGameTicket[], nextWarning = '') => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (timerRef.current) window.clearInterval(timerRef.current)

    if (reducedMotion) {
      setOtherTickets(next)
      setWarning(nextWarning)
      return
    }

    setGenerating(true)
    setOtherTickets([])
    setWarning('')

    timerRef.current = window.setInterval(() => {
      const preview = generateOtherGame(game as Exclude<WelfareGameId, 'ssq'>, 1, {
        happyPickCount,
        fc3dMode,
        mode: 'quick',
      }).tickets[0]
      if (preview) setRollingOther(preview)
    }, 100)

    finishTimerRef.current = window.setTimeout(() => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current)
        timerRef.current = null
      }
      finishTimerRef.current = null
      setGenerating(false)
      setOtherTickets(next)
      setWarning(nextWarning)
      window.setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 120)
    }, 850)
  }

  const run = () => {
    if (game !== 'ssq') {
      const result = generateOtherGame(game, constraints.count, {
        happyPickCount,
        fc3dMode,
        mode: otherMode,
        constraints: otherConstraints,
        dan: otherDan,
        tuo: otherTuo,
        multiple: otherMultiple,
      })

      if (!result.tickets.length) {
        setOtherTickets([])
        setWarning(result.warning ?? '没有生成到可用组合。')
        flash('当前玩法没有生成到组合')
        return
      }

      showOtherResult(result.tickets, result.warning ?? '')
      return
    }

    const result =
      mode === 'quick'
        ? quickGenerate(constraints.count, historySet)
        : mode === 'conditional'
          ? generateConditional(constraints, historySet)
          : mode === 'dantuo'
            ? expandDantuo(dan, tuo, dantuoBlue)
            : expandMultiple(multipleRed, multipleBlue)

    if (!result.tickets.length) {
      setTickets([])
      setWarning(result.warning ?? '没有生成到符合条件的组合。')
      flash('当前条件没有可用组合')
      return
    }

    showResult(result.tickets, result.warning ?? '')
  }

  const copyAll = async () => {
    if (!resultCount) return
    const text = game === 'ssq' ? ticketsText(tickets) : otherTicketsText(otherTickets)

    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      textarea.remove()
    }

    flash(`已复制 ${resultCount} 注组合`)
  }

  const exportImage = () => {
    if (!resultCount) return

    const shareTickets: ShareTicket[] = game === 'ssq'
      ? tickets.map(ticket => ({ numbers: ticket.reds, secondary: [ticket.blue] }))
      : otherTickets.map(ticket => ({
          numbers: ticket.numbers,
          singleDigit: ticket.game === 'fc3d',
        }))

    const colorByGame: Record<WelfareGameId, string> = {
      ssq: '#e95662',
      qlc: '#d89b2b',
      fc3d: '#7c5ce6',
      kl8: '#1e9b82',
    }

    downloadShareImage(shareTickets, {
      gameName: activeGame.name,
      rule: game === 'fc3d'
        ? `${activeGame.rule} · ${fc3dMode === 'direct' ? '单选' : fc3dMode === 'group3' ? '组选3' : '组选6'} · ${selectedOtherMode.label}`
        : game === 'kl8'
          ? `01–80 中选择 ${happyPickCount} 个号码（选${happyPickCount}） · ${selectedOtherMode.label}`
          : game === 'qlc'
            ? `${activeGame.rule} · ${selectedOtherMode.label}`
            : activeGame.rule,
      filename: game,
      primaryColor: colorByGame[game],
      secondaryColor: '#4f7fe8',
    })
    flash('分享图已生成')
  }

  const toggleFavorite = (ticket: Ticket) => {
    const key = ticketKey(ticket)
    setFavorites(prev => {
      const exists = prev.some(item => ticketKey(item) === key)
      return exists
        ? prev.filter(item => ticketKey(item) !== key)
        : [...prev, ticket]
    })
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark">回</div>
          <div>
            <p className="eyebrow">REGRESSION LINE WELFARE LOTTERY CLUB</p>
            <h1>回归线福彩俱乐部</h1>
          </div>
        </div>
        <button className="icon-button" type="button" onClick={() => setDark(v => !v)} aria-label="切换主题">
          {dark ? '☀' : '☾'}
        </button>
      </header>

      <section className="hero">
        <div>
          <span className="hero-chip">中国福利彩票 · 多玩法号码工具</span>
          <h2>一站切换玩法，<br /><span>轻松生成号码。</span></h2>
          <p>支持双色球、七乐彩、福彩3D与快乐8。所有号码都在浏览器本地生成，结构分析只用于组合观察，不用于预测开奖。</p>
        </div>
        <div className="hero-orbit" aria-hidden="true">
          <span className="orbit-ball red">08</span>
          <span className="orbit-ball red">19</span>
          <span className="orbit-ball blue">12</span>
          <span className="orbit-ring" />
          <strong>{GAME_ICONS[game]}</strong>
        </div>
      </section>

      <nav className="game-tabs" aria-label="福彩玩法">
        {WELFARE_GAMES.map(item => (
          <button
            key={item.id}
            type="button"
            className={game === item.id ? 'game-tab active' : 'game-tab'}
            onClick={() => switchGame(item.id)}
          >
            <span className="game-tab-icon">{GAME_ICONS[item.id]}</span>
            <span className="game-tab-copy">
              <strong>{item.name}</strong>
              <small>{item.note}</small>
            </span>
          </button>
        ))}
      </nav>

      <nav className="mode-tabs" aria-label={`${activeGame.name}生成模式`}>
        {game === 'ssq'
          ? MODES.map(item => (
              <button
                key={item.id}
                className={mode === item.id ? 'mode-tab active' : 'mode-tab'}
                type="button"
                onClick={() => {
                  setMode(item.id)
                  setTickets([])
                  setWarning('')
                  setResultView('visual')
                }}
              >
                <span>{item.icon}</span>
                <strong>{item.label}</strong>
                <small>{item.note}</small>
              </button>
            ))
          : otherModeOptions.map(item => (
              <button
                key={item.id}
                className={otherMode === item.id ? 'mode-tab active' : 'mode-tab'}
                type="button"
                onClick={() => {
                  setOtherMode(item.id)
                  setOtherTickets([])
                  setWarning('')
                  setResultView('visual')
                }}
              >
                <span>{item.icon}</span>
                <strong>{item.label}</strong>
                <small>{item.note}</small>
              </button>
            ))}
      </nav>

      <section className="workspace">
        <aside className="control-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">GENERATOR</p>
              <h3>{game === 'ssq' ? selectedMode.label : selectedOtherMode.label}</h3>
            </div>
            <span className="mode-badge">{game === 'ssq' ? selectedMode.icon : selectedOtherMode.icon}</span>
          </div>

          {((game === 'ssq' && (mode === 'quick' || mode === 'conditional')) ||
            (game !== 'ssq' && (otherMode === 'quick' || otherMode === 'conditional'))) && (
            <>
              <label className="field">
                <span>生成数量</span>
                <div className="stepper">
                  <button type="button" aria-label="减少生成数量" onClick={() => setCount(constraints.count - 1)}>−</button>
                  <input
                    className="count-input"
                    aria-label="生成数量"
                    aria-describedby="count-help"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="off"
                    value={countDraft}
                    onChange={e => {
                      const raw = e.target.value.replace(/\D/g, '').slice(0, 3)
                      setCountDraft(raw)
                      if (raw) {
                        const parsed = Number.parseInt(raw, 10)
                        if (Number.isFinite(parsed) && parsed >= 1) {
                          setConstraints(current => ({ ...current, count: Math.min(200, parsed) }))
                        }
                      }
                    }}
                    onBlur={commitCountDraft}
                    onKeyDown={e => {
                      if (e.key === 'Enter') e.currentTarget.blur()
                    }}
                  />
                  <button type="button" aria-label="增加生成数量" onClick={() => setCount(constraints.count + 1)}>+</button>
                </div>
                <small id="count-help" className="field-help">1–200 注</small>
              </label>
              <div className="quick-counts">
                {[1, 5, 10, 20].map(n => (
                  <button key={n} type="button" onClick={() => setCount(n)}>{n} 注</button>
                ))}
              </div>
            </>
          )}

          {game !== 'ssq' && (
            <div className="game-settings-card">
              <div className="game-rule">
                <span>玩法规则</span>
                <strong>{activeGame.rule}</strong>
              </div>

              {game === 'fc3d' && (
                <label className="field">
                  <span>投注方式</span>
                  <select
                    value={fc3dMode}
                    onChange={e => {
                      const nextMode = e.target.value as Fc3dMode
                      setFc3dMode(nextMode)
                      setOtherConstraints(current => ({
                        ...current,
                        required: current.required.slice(0, nextMode === 'group3' ? 2 : 3),
                      }))
                      setOtherTickets([])
                      setWarning('')
                      setResultView('visual')
                    }}
                  >
                    <option value="direct">单选 · 按位排列</option>
                    <option value="group3">组选3 · 两同一不同</option>
                    <option value="group6">组选6 · 三个不同</option>
                  </select>
                </label>
              )}

              {game === 'kl8' && (
                <label className="field">
                  <span>选择玩法</span>
                  <select
                    value={happyPickCount}
                    onChange={e => {
                      const nextPickCount = Number(e.target.value)
                      setHappyPickCount(nextPickCount)
                      setOtherConstraints(current => ({
                        ...current,
                        required: current.required.slice(0, nextPickCount),
                        oddCount: current.oddCount !== undefined && current.oddCount > nextPickCount ? undefined : current.oddCount,
                        bigCount: current.bigCount !== undefined && current.bigCount > nextPickCount ? undefined : current.bigCount,
                      }))
                      setOtherDan(current => current.slice(0, Math.max(0, nextPickCount - 1)))
                      if (nextPickCount === 1 && otherMode === 'dantuo') setOtherMode('quick')
                      setOtherTickets([])
                      setWarning('')
                      setResultView('visual')
                    }}
                  >
                    {Array.from({ length: 10 }, (_, index) => index + 1).map(value => (
                      <option key={value} value={value}>选{value} · 每注 {value} 个号码</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}

          {game !== 'ssq' && otherMode === 'conditional' && (
            <div className="constraint-stack">
              <div className="two-col">
                <label className="field">
                  <span>奇数个数</span>
                  <select
                    value={otherConstraints.oddCount ?? 'any'}
                    onChange={e => setOtherConstraints(current => ({
                      ...current,
                      oddCount: e.target.value === 'any' ? undefined : Number(e.target.value),
                    }))}
                  >
                    <option value="any">不限</option>
                    {Array.from({ length: otherPickCount + 1 }, (_, index) => index).map(value => (
                      <option key={value} value={value}>{value} 个</option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>大号个数 · ≥ {otherBigFrom}</span>
                  <select
                    value={otherConstraints.bigCount ?? 'any'}
                    onChange={e => setOtherConstraints(current => ({
                      ...current,
                      bigCount: e.target.value === 'any' ? undefined : Number(e.target.value),
                    }))}
                  >
                    <option value="any">不限</option>
                    {Array.from({ length: otherPickCount + 1 }, (_, index) => index).map(value => (
                      <option key={value} value={value}>{value} 个</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="two-col">
                <label className="field">
                  <span>和值下限</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    placeholder="不限"
                    value={otherConstraints.sumMin ?? ''}
                    onChange={e => setOtherConstraints(current => ({
                      ...current,
                      sumMin: e.target.value ? Number(e.target.value) : undefined,
                    }))}
                  />
                </label>
                <label className="field">
                  <span>和值上限</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    placeholder="不限"
                    value={otherConstraints.sumMax ?? ''}
                    onChange={e => setOtherConstraints(current => ({
                      ...current,
                      sumMax: e.target.value ? Number(e.target.value) : undefined,
                    }))}
                  />
                </label>
              </div>

              <div className="two-col">
                <label className="field">
                  <span>跨度下限</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    placeholder="不限"
                    value={otherConstraints.spanMin ?? ''}
                    onChange={e => setOtherConstraints(current => ({
                      ...current,
                      spanMin: e.target.value ? Number(e.target.value) : undefined,
                    }))}
                  />
                </label>
                <label className="field">
                  <span>跨度上限</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    placeholder="不限"
                    value={otherConstraints.spanMax ?? ''}
                    onChange={e => setOtherConstraints(current => ({
                      ...current,
                      spanMax: e.target.value ? Number(e.target.value) : undefined,
                    }))}
                  />
                </label>
              </div>

              {game !== 'fc3d' && (
                <label className="field">
                  <span>最多连号对数</span>
                  <select
                    value={otherConstraints.maxConsecutivePairs ?? 'any'}
                    onChange={e => setOtherConstraints(current => ({
                      ...current,
                      maxConsecutivePairs: e.target.value === 'any' ? undefined : Number(e.target.value),
                    }))}
                  >
                    <option value="any">不限</option>
                    {Array.from({ length: otherPickCount }, (_, index) => index).map(value => (
                      <option key={value} value={value}>最多 {value} 对</option>
                    ))}
                  </select>
                </label>
              )}

              <button type="button" className="text-button" onClick={() => setShowOtherAdvanced(value => !value)}>
                {showOtherAdvanced ? '收起必选 / 排除号码 ↑' : '设置必选 / 排除号码 ↓'}
              </button>

              {showOtherAdvanced && (
                <div className="advanced-box">
                  <div className="picker-section">
                    <div className="picker-title">
                      <strong>{game === 'fc3d' ? '至少包含数字' : '必选号码'}</strong>
                      <span>{otherConstraints.required.length}/{otherRequiredLimit}</span>
                    </div>
                    <NumberGrid
                      min={otherNumberMin}
                      max={otherNumberMax}
                      tone={activeOtherTone}
                      pad={game !== 'fc3d'}
                      dense={game === 'kl8'}
                      selected={otherConstraints.required}
                      disabledNumbers={otherConstraints.excluded}
                      onToggle={value => setOtherConstraints(current => ({
                        ...current,
                        required: current.required.includes(value)
                          ? current.required.filter(number => number !== value)
                          : current.required.length < otherRequiredLimit
                            ? [...current.required, value].sort((a, b) => a - b)
                            : current.required,
                      }))}
                    />
                  </div>

                  <div className="picker-section">
                    <div className="picker-title">
                      <strong>排除号码</strong>
                      <span>{otherConstraints.excluded.length} 个</span>
                    </div>
                    <NumberGrid
                      min={otherNumberMin}
                      max={otherNumberMax}
                      tone={activeOtherTone}
                      pad={game !== 'fc3d'}
                      dense={game === 'kl8'}
                      selected={otherConstraints.excluded}
                      disabledNumbers={otherConstraints.required}
                      onToggle={value => setOtherConstraints(current => ({
                        ...current,
                        excluded: toggleNumber(current.excluded, value),
                      }))}
                    />
                  </div>

                  <button
                    type="button"
                    className="subtle-reset"
                    onClick={() => setOtherConstraints({ ...EMPTY_OTHER_CONSTRAINTS })}
                  >
                    清空全部条件
                  </button>
                </div>
              )}
            </div>
          )}

          {game !== 'ssq' && otherMode === 'dantuo' && game !== 'fc3d' && (
            <div className="constraint-stack generation-builder">
              <div className="builder-intro">
                <strong>胆码 + 拖码</strong>
                <span>胆码固定进入每一注，再从拖码中补足 {otherPickCount} 个号码。</span>
              </div>
              <div className="picker-section">
                <div className="picker-title">
                  <strong>胆码</strong>
                  <span>{otherDan.length}/{Math.max(1, otherPickCount - 1)}</span>
                </div>
                <NumberGrid
                  max={otherNumberMax}
                  tone={activeOtherTone}
                  dense={game === 'kl8'}
                  selected={otherDan}
                  disabledNumbers={otherTuo}
                  onToggle={value => setOtherDan(current =>
                    current.includes(value)
                      ? current.filter(number => number !== value)
                      : current.length < otherPickCount - 1
                        ? [...current, value].sort((a, b) => a - b)
                        : current
                  )}
                />
              </div>
              <div className="picker-section">
                <div className="picker-title">
                  <strong>拖码</strong>
                  <span>{otherTuo.length} 个</span>
                </div>
                <NumberGrid
                  max={otherNumberMax}
                  tone={activeOtherTone}
                  dense={game === 'kl8'}
                  selected={otherTuo}
                  disabledNumbers={otherDan}
                  onToggle={value => setOtherTuo(current => toggleNumber(current, value))}
                />
              </div>
              <div className="calc-chip">
                {otherDantuoCount} 注 · ¥{otherDantuoCount * PRICE_PER_TICKET}
                {otherDantuoCount > 1000 && <small>页面最多展开前 1000 注</small>}
              </div>
            </div>
          )}

          {game !== 'ssq' && otherMode === 'multiple' && game !== 'fc3d' && (
            <div className="constraint-stack generation-builder">
              <div className="builder-intro">
                <strong>组合展开</strong>
                <span>从选号池中自动展开所有 {otherPickCount} 个号码的组合。</span>
              </div>
              <div className="picker-section">
                <div className="picker-title">
                  <strong>选号池</strong>
                  <span>{otherMultiple.length} 个</span>
                </div>
                <NumberGrid
                  max={otherNumberMax}
                  tone={activeOtherTone}
                  dense={game === 'kl8'}
                  selected={otherMultiple}
                  onToggle={value => setOtherMultiple(current => toggleNumber(current, value))}
                />
              </div>
              <div className="calc-chip">
                {otherMultipleCount} 注 · ¥{otherMultipleCount * PRICE_PER_TICKET}
                {otherMultipleCount > 1000 && <small>页面最多展开前 1000 注</small>}
              </div>
            </div>
          )}

          {game === 'ssq' && mode === 'conditional' && (
            <div className="constraint-stack">
              <div className="two-col">
                <label className="field">
                  <span>奇数个数</span>
                  <select
                    value={constraints.oddCount ?? 'any'}
                    onChange={e => setConstraints(c => ({ ...c, oddCount: e.target.value === 'any' ? undefined : Number(e.target.value) }))}
                  >
                    <option value="any">不限</option>
                    {[0, 1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n} 个</option>)}
                  </select>
                </label>
                <label className="field">
                  <span>大号个数</span>
                  <select
                    value={constraints.bigCount ?? 'any'}
                    onChange={e => setConstraints(c => ({ ...c, bigCount: e.target.value === 'any' ? undefined : Number(e.target.value) }))}
                  >
                    <option value="any">不限</option>
                    {[0, 1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n} 个</option>)}
                  </select>
                </label>
              </div>

              <div className="two-col">
                <label className="field">
                  <span>和值下限</span>
                  <input type="number" placeholder="不限" value={constraints.sumMin ?? ''} onChange={e => setConstraints(c => ({ ...c, sumMin: e.target.value ? Number(e.target.value) : undefined }))} />
                </label>
                <label className="field">
                  <span>和值上限</span>
                  <input type="number" placeholder="不限" value={constraints.sumMax ?? ''} onChange={e => setConstraints(c => ({ ...c, sumMax: e.target.value ? Number(e.target.value) : undefined }))} />
                </label>
              </div>

              <label className="field">
                <span>三区比例</span>
                <select value={constraints.zonePreset} onChange={e => setConstraints(c => ({ ...c, zonePreset: e.target.value as ZonePreset }))}>
                  <option value="any">不限</option>
                  <option value="2-2-2">2 : 2 : 2</option>
                  <option value="1-2-3">1 : 2 : 3</option>
                  <option value="2-1-3">2 : 1 : 3</option>
                  <option value="3-2-1">3 : 2 : 1</option>
                </select>
              </label>

              <button type="button" className="text-button" onClick={() => setShowAdvanced(v => !v)}>
                {showAdvanced ? '收起号码约束 ↑' : '展开号码约束 ↓'}
              </button>

              {showAdvanced && (
                <div className="advanced-box">
                  <div className="picker-section">
                    <div className="picker-title"><strong>必选红球</strong><span>{constraints.requiredRed.length}/6</span></div>
                    <NumberGrid
                      max={RED_MAX}
                      selected={constraints.requiredRed}
                      disabledNumbers={constraints.excludedRed}
                      onToggle={n => setConstraints(c => ({
                        ...c,
                        requiredRed: c.requiredRed.includes(n)
                          ? c.requiredRed.filter(x => x !== n)
                          : c.requiredRed.length < 6
                            ? [...c.requiredRed, n].sort((a, b) => a - b)
                            : c.requiredRed,
                      }))}
                    />
                  </div>

                  <div className="picker-section">
                    <div className="picker-title"><strong>排除红球</strong><span>{constraints.excludedRed.length} 个</span></div>
                    <NumberGrid
                      max={RED_MAX}
                      selected={constraints.excludedRed}
                      disabledNumbers={constraints.requiredRed}
                      onToggle={n => setConstraints(c => ({ ...c, excludedRed: toggleNumber(c.excludedRed, n) }))}
                    />
                  </div>

                  <div className="picker-section">
                    <div className="picker-title"><strong>排除蓝球</strong><span>{constraints.excludedBlue.length} 个</span></div>
                    <NumberGrid
                      max={BLUE_MAX}
                      blue
                      selected={constraints.excludedBlue}
                      onToggle={n => setConstraints(c => ({ ...c, excludedBlue: toggleNumber(c.excludedBlue, n) }))}
                    />
                  </div>

                  <label className="field">
                    <span>最多相邻对数</span>
                    <select
                      value={constraints.maxConsecutivePairs ?? 'any'}
                      onChange={e => setConstraints(c => ({ ...c, maxConsecutivePairs: e.target.value === 'any' ? undefined : Number(e.target.value) }))}
                    >
                      <option value="any">不限</option>
                      <option value="0">不允许连号</option>
                      <option value="1">最多 1 对</option>
                      <option value="2">最多 2 对</option>
                    </select>
                  </label>
                </div>
              )}
            </div>
          )}

          {game === 'ssq' && mode === 'dantuo' && (
            <div className="constraint-stack">
              <div className="picker-section">
                <div className="picker-title"><strong>胆码</strong><span>{dan.length}/5</span></div>
                <NumberGrid
                  max={RED_MAX}
                  selected={dan}
                  disabledNumbers={tuo}
                  onToggle={n => setDan(prev => prev.includes(n) ? prev.filter(x => x !== n) : prev.length < 5 ? [...prev, n].sort((a, b) => a - b) : prev)}
                />
              </div>

              <div className="picker-section">
                <div className="picker-title"><strong>拖码</strong><span>{tuo.length} 个</span></div>
                <NumberGrid max={RED_MAX} selected={tuo} disabledNumbers={dan} onToggle={n => setTuo(prev => toggleNumber(prev, n))} />
              </div>

              <div className="picker-section">
                <div className="picker-title"><strong>蓝球</strong><span>{dantuoBlue.length} 个</span></div>
                <NumberGrid max={BLUE_MAX} blue selected={dantuoBlue} onToggle={n => setDantuoBlue(prev => toggleNumber(prev, n))} />
              </div>

              <div className="calc-chip">{dantuoCount} 注 · ¥{dantuoCount * PRICE_PER_TICKET}</div>
            </div>
          )}

          {game === 'ssq' && mode === 'multiple' && (
            <div className="constraint-stack">
              <div className="picker-section">
                <div className="picker-title"><strong>红球复式</strong><span>{multipleRed.length} 个</span></div>
                <NumberGrid max={RED_MAX} selected={multipleRed} onToggle={n => setMultipleRed(prev => toggleNumber(prev, n))} />
              </div>

              <div className="picker-section">
                <div className="picker-title"><strong>蓝球复式</strong><span>{multipleBlue.length} 个</span></div>
                <NumberGrid max={BLUE_MAX} blue selected={multipleBlue} onToggle={n => setMultipleBlue(prev => toggleNumber(prev, n))} />
              </div>

              <div className="calc-chip">{multipleCount} 注 · ¥{multipleCount * PRICE_PER_TICKET}</div>
            </div>
          )}

          <button className="generate-button" type="button" onClick={run} disabled={generating}>
            <span>{generating ? '生成中…' : game === 'ssq' && mode === 'quick' ? '摇出好运组合' : `生成${activeGame.shortName}号码`}</span>
            <b>{generating ? '◌' : '→'}</b>
          </button>
          <p className="probability-note">号码生成仅用于组合与记录；随机、筛选或结构分析都不代表预测未来开奖结果。</p>
        </aside>

        <div className="result-panel" ref={resultRef}>
          <div className="result-header">
            <div>
              <p className="eyebrow">RESULTS</p>
              <h3>{generating ? `${activeGame.name}号码正在生成…` : resultCount ? `生成了 ${resultCount} 注` : '等待你的组合'}</h3>
            </div>

          </div>

          {generating ? (
            <div className="lottery-machine" aria-live="polite">
              <div className="machine-window">
                <div className="floating-dots" aria-hidden="true">
                  {Array.from({ length: 12 }, (_, i) => <i key={i} />)}
                </div>
                {game === 'ssq' ? (
                  <div className="rolling-row">
                    {rolling.reds.map((n, i) => <Ball key={i} value={n} delay={i * 50} />)}
                    <span className="plus">+</span>
                    <Ball value={rolling.blue} blue delay={320} />
                  </div>
                ) : (
                  <div className="rolling-row rolling-other">
                    {(rollingOther?.numbers ?? []).map((n, i) => (
                      <Ball
                        key={`${i}-${n}`}
                        value={n}
                        tone={activeOtherTone}
                        pad={game !== 'fc3d'}
                        delay={i * 45}
                      />
                    ))}
                  </div>
                )}
                <p>正在生成 {activeGame.name} 号码…</p>
              </div>
            </div>
          ) : resultCount > 0 ? (
            game === 'ssq' ? (
            <>
              <div className="summary-strip">
                <span><b>{tickets.length}</b> 注组合</span>
                <span><b>¥{totalPrice}</b> 理论票面金额</span>
              </div>

              {warning && <div className="warning">{warning}</div>}

              <div className="result-viewbar" aria-label="结果显示方式">
                <div>
                  <strong>生成号码</strong>
                  <span>默认保持简洁，需要时再展开统计分析</span>
                </div>
                <div className="view-switch">
                  <button
                    type="button"
                    className={resultView === 'visual' ? 'active' : ''}
                    onClick={() => setResultView('visual')}
                  >
                    号码组合
                  </button>
                  <button
                    type="button"
                    className={resultView === 'details' ? 'active' : ''}
                    onClick={() => setResultView('details')}
                  >
                    统计分析
                  </button>
                </div>
              </div>

              {resultView === 'visual' ? (
                <div className="compact-ticket-grid" aria-label="全部生成组合">
                  {tickets.map((ticket, index) => (
                    <article
                      className="compact-ticket"
                      key={ticketKey(ticket)}
                      style={{ '--ticket-delay': `${Math.min(index, 18) * 32}ms` } as CSSProperties}
                    >
                      <span className="compact-index">{String(index + 1).padStart(2, '0')}</span>
                      <div className="compact-balls">
                        {ticket.reds.map((n, i) => <Ball key={n} value={n} delay={i * 35} />)}
                        <span className="plus">+</span>
                        <Ball value={ticket.blue} blue delay={230} />
                      </div>
                      <button
                        className={favorites.some(item => ticketKey(item) === ticketKey(ticket)) ? 'favorite compact-favorite active' : 'favorite compact-favorite'}
                        type="button"
                        onClick={() => toggleFavorite(ticket)}
                        aria-label={favorites.some(item => ticketKey(item) === ticketKey(ticket)) ? '取消收藏' : '收藏'}
                      >
                        {favorites.some(item => ticketKey(item) === ticketKey(ticket)) ? '★' : '☆'}
                      </button>
                    </article>
                  ))}
                </div>
              ) : (
                <>
                  <section className="frequency-board" aria-label="号码出现频次">
                    <div className="frequency-section">
                      <div className="frequency-heading">
                        <strong>红球分布</strong>
                        <span>数字越亮，当前批次出现次数越多</span>
                      </div>
                      <div className="heat-grid heat-grid-red">
                        {redFrequency.map((count, index) => (
                          <div
                            key={index}
                            className="heat-cell red-heat"
                            style={{ '--heat': String(count / maxRedFrequency) } as CSSProperties}
                            title={`${formatNumber(index + 1)} 出现 ${count} 次`}
                          >
                            <b>{formatNumber(index + 1)}</b>
                            <small>{count}</small>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="frequency-section">
                      <div className="frequency-heading">
                        <strong>蓝球分布</strong>
                        <span>共 {tickets.length} 个蓝球</span>
                      </div>
                      <div className="heat-grid heat-grid-blue">
                        {blueFrequency.map((count, index) => (
                          <div
                            key={index}
                            className="heat-cell blue-heat"
                            style={{ '--heat': String(count / maxBlueFrequency) } as CSSProperties}
                            title={`${formatNumber(index + 1)} 出现 ${count} 次`}
                          >
                            <b>{formatNumber(index + 1)}</b>
                            <small>{count}</small>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>

                  <div className="ticket-list details-grid">
                {tickets.map((ticket, index) => {
                  const metrics = calculateMetrics(ticket.reds)
                  const favorite = favorites.some(item => ticketKey(item) === ticketKey(ticket))

                  return (
                    <article
                      className="ticket-card"
                      key={ticketKey(ticket)}
                      style={{ '--ticket-delay': `${Math.min(index, 15) * 45}ms` } as CSSProperties}
                    >
                      <div className="ticket-index">{String(index + 1).padStart(2, '0')}</div>
                      <div className="ticket-main">
                        <div className="ticket-balls">
                          {ticket.reds.map((n, i) => <Ball key={n} value={n} delay={i * 55} />)}
                          <span className="plus">+</span>
                          <Ball value={ticket.blue} blue delay={360} />
                        </div>
                        <div className="metric-line">
                          <span>奇偶 {metrics.odd}:{metrics.even}</span>
                          <span>大小 {metrics.big}:{metrics.small}</span>
                          <span>三区 {metrics.zones.join(':')}</span>
                          <span>和值 {metrics.sum}</span>
                          <span>跨度 {metrics.span}</span>
                          <span>AC {metrics.ac}</span>
                        </div>
                      </div>
                      <button
                        className={favorite ? 'favorite active' : 'favorite'}
                        type="button"
                        onClick={() => toggleFavorite(ticket)}
                        aria-label={favorite ? '取消收藏' : '收藏'}
                      >
                        {favorite ? '★' : '☆'}
                      </button>
                    </article>
                  )
                })}
                  </div>
                </>
              )}

              <div className="result-actions result-actions-bottom">
                <button type="button" className="secondary-action" onClick={copyAll}>复制全部号码</button>
                <button type="button" className="primary-action" onClick={exportImage}>下载分享图片</button>
              </div>
            </>
            ) : (
              <>
                <div className="summary-strip">
                  <span><b>{resultCount}</b> 注 {activeGame.name}</span>
                  <span><b>¥{totalPrice}</b> 理论票面金额</span>
                  <span><b>{selectedOtherMode.label}</b> 生成逻辑</span>
                  {game === 'kl8' && <span><b>选{happyPickCount}</b> 当前玩法</span>}
                  {game === 'fc3d' && (
                    <span><b>{fc3dMode === 'direct' ? '单选' : fc3dMode === 'group3' ? '组选3' : '组选6'}</b> 当前方式</span>
                  )}
                </div>

                {warning && <div className="warning">{warning}</div>}

                <div className="result-viewbar">
                  <div>
                    <strong>{activeGame.name}号码</strong>
                    <span>{selectedOtherMode.label} · {activeGame.rule}</span>
                  </div>
                  <div className="view-switch">
                    <button
                      type="button"
                      className={resultView === 'visual' ? 'active' : ''}
                      onClick={() => setResultView('visual')}
                    >
                      号码组合
                    </button>
                    <button
                      type="button"
                      className={resultView === 'details' ? 'active' : ''}
                      onClick={() => setResultView('details')}
                    >
                      结构分析
                    </button>
                  </div>
                </div>

                <div className={game === 'kl8' ? 'other-ticket-grid wide' : 'other-ticket-grid'}>
                  {otherTickets.map((ticket, index) => {
                    const metrics = analyzeOtherNumbers(ticket.game, ticket.numbers)
                    return (
                      <article
                        className={[
                          'other-ticket',
                          resultView === 'details' ? 'with-metrics' : '',
                          index > 30 ? 'ticket-static' : '',
                        ].filter(Boolean).join(' ')}
                        key={`${ticket.game}-${ticket.numbers.join('-')}`}
                      >
                        <span className="compact-index">{String(index + 1).padStart(2, '0')}</span>
                        <div className="other-ticket-main">
                          <div className="other-ticket-balls">
                            {ticket.numbers.map((n, i) => (
                              <Ball
                                key={`${i}-${n}`}
                                value={n}
                                tone={GAME_TONES[ticket.game]}
                                pad={ticket.game !== 'fc3d'}
                                delay={i * 30}
                              />
                            ))}
                          </div>
                          {resultView === 'details' && (
                            <div className="metric-line other-metrics">
                              <span>奇偶 {metrics.odd}:{metrics.even}</span>
                              <span>大小 {metrics.big}:{metrics.small}</span>
                              <span>和值 {metrics.sum}</span>
                              <span>跨度 {metrics.span}</span>
                              {ticket.game !== 'fc3d' && <span>连号 {metrics.consecutivePairs} 对</span>}
                            </div>
                          )}
                        </div>
                      </article>
                    )
                  })}
                </div>

                <div className="result-actions result-actions-bottom">
                  <button type="button" className="secondary-action" onClick={copyAll}>复制全部号码</button>
                  <button type="button" className="primary-action" onClick={exportImage}>下载分享图片</button>
                </div>
              </>
            )
          ) : (
            <div className="empty-state">
              <div className="empty-machine">
                <span className="mini-ball red">?</span>
                <span className="mini-ball red">?</span>
                <span className="mini-ball blue">?</span>
              </div>
              <h4>还没有号码</h4>
              <p>选择一个福彩玩法，设定数量，然后生成号码。</p>
            </div>
          )}

          {!generating && warning && !resultCount && <div className="warning large">{warning}</div>}
        </div>
      </section>

      <section className="insight-grid">
        <div className="insight-card">
          <span>⌁</span>
          <div><strong>多玩法切换</strong><p>双色球、七乐彩、福彩3D与快乐8共用一个简洁的生成与分享界面。</p></div>
        </div>
        <div className="insight-card">
          <span>◫</span>
          <div><strong>一键带走</strong><p>复制纯文本，或生成适合聊天分享的高清 PNG 组合图。</p></div>
        </div>
        <div className="insight-card">
          <span>◇</span>
          <div><strong>独立生成逻辑</strong><p>不同彩种使用各自的随机、条件和组合逻辑，不把双色球规则硬套到其他玩法。</p></div>
        </div>
      </section>

      {game === 'ssq' && favorites.length > 0 && (
        <section className="favorites-section">
          <div className="section-heading">
            <div><p className="eyebrow">SAVED LOCALLY</p><h3>我的收藏</h3></div>
            <button type="button" className="text-button" onClick={() => setFavorites([])}>清空收藏</button>
          </div>
          <div className="favorite-row">
            {favorites.slice(-12).reverse().map(ticket => (
              <button key={ticketKey(ticket)} type="button" className="favorite-ticket" onClick={() => toggleFavorite(ticket)}>
                {ticket.reds.map(formatNumber).join(' · ')} <b>+ {formatNumber(ticket.blue)}</b>
              </button>
            ))}
          </div>
        </section>
      )}

      <footer>
        <p>回归线福彩俱乐部 · 福利彩票号码组合与记录工具</p>
        <p>理性购彩，量力而行。任何筛选条件都不会改变单注合法组合的理论中奖概率。</p>
      </footer>

      {toast && <div className="toast" role="status">{toast}</div>}
    </main>
  )
}
